import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { usePromptContext } from '../src/context/PromptContext';
import type { ItemKind, Prompt } from '../types';
import {
  StarIcon,
  TrashIcon,
  DownloadIcon,
  ClipboardCopyIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  CopyPlusIcon,
  StampIcon,
  FolderMoveIcon,
  MoreVerticalIcon,
  KindGlyph,
} from '../constants';
import TagManager from './TagManager';
import PromptActions from './PromptActions';
import VersionHistory from './VersionHistory';
import WorkflowEditor from './WorkflowEditor';
import { SkillHeader, SkillFiles, SkillPreview } from './SkillEditor';
import { findPromptAndParentFolder, getFolderPath, getAllPrompts } from '../utils/fileSystemUtils';
import { ITEM_KINDS, KIND_META, getKind, buildSkillMarkdown, buildWorkflowMarkdown, getSkillSlug, slugify } from '../utils/itemKinds';
import { v4 as uuidv4 } from 'uuid';

/** Felder, die der Editor besitzt. Zähler wie useCount bleiben unangetastet. */
const EDITABLE_FIELDS = ['name', 'content', 'description', 'isFavorite', 'tags', 'versions', 'kind', 'slug', 'files', 'steps', 'updatedAt'] as const;
type EditableUpdate = Partial<Prompt> & { id: string };

const pickEditable = (p: Prompt): EditableUpdate => {
  const out: EditableUpdate = { id: p.id };
  EDITABLE_FIELDS.forEach(k => { (out as any)[k] = (p as any)[k]; });
  return out;
};

const isSameEditable = (a: Prompt, b: Prompt) =>
  EDITABLE_FIELDS.every(k => k === 'updatedAt' || JSON.stringify((a as any)[k] ?? null) === JSON.stringify((b as any)[k] ?? null));

const Breadcrumbs: React.FC<{ fileSystem: any; folderId: string; onNavigate: (id: string) => void }> = ({ fileSystem, folderId, onNavigate }) => {
  const path = useMemo(() => getFolderPath(fileSystem, folderId), [fileSystem, folderId]);
  if (path.length === 0) return null;
  return (
    <nav aria-label="Ordnerpfad" className="flex items-center gap-1 text-xs text-graphite min-w-0 overflow-hidden">
      {path.map((folder, i) => (
        <React.Fragment key={folder.id}>
          {i > 0 && <ChevronRightIcon className="h-3 w-3 shrink-0 opacity-60" />}
          <button onClick={() => onNavigate(folder.id)} className="hover:text-ink hover:underline underline-offset-2 truncate max-w-[12rem]">
            {folder.name}
          </button>
        </React.Fragment>
      ))}
    </nav>
  );
};

const KindSwitch: React.FC<{ value: ItemKind; onChange: (k: ItemKind) => void }> = ({ value, onChange }) => (
  <div className="inline-flex rounded-md border border-rule bg-sheet p-0.5" role="radiogroup" aria-label="Art des Eintrags">
    {ITEM_KINDS.map(kind => {
      const active = value === kind;
      return (
        <button
          key={kind}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => !active && onChange(kind)}
          className={`flex items-center gap-1.5 h-7 px-2.5 rounded text-xs transition-colors ${
            active ? `bg-raised shadow-sm font-medium ${KIND_META[kind].text}` : 'text-graphite hover:text-ink'
          }`}
          title={active ? `Ist ein ${KIND_META[kind].label}` : `In ${KIND_META[kind].label} umwandeln`}
        >
          <KindGlyph kind={kind} className="h-3.5 w-3.5" />
          <span className={active ? '' : 'hidden xl:inline'}>{KIND_META[kind].label}</span>
        </button>
      );
    })}
  </div>
);

interface PromptEditorProps {
  prompt: Prompt;
  isMobile: boolean;
  onClose: () => void;
  onSave?: (updates: EditableUpdate) => void;
  onDelete?: () => void;
  onDuplicate?: (current?: Prompt) => void;
  onExport?: (current?: Prompt) => void;
  onTrackUse?: (id: string) => void;
}

const AUTOSAVE_DELAY = 1200;

const PromptEditor: React.FC<PromptEditorProps> = ({
  prompt,
  isMobile,
  onClose,
  onSave,
  onDelete,
  onDuplicate,
  onExport,
  onTrackUse,
}) => {
  const {
    showToast,
    searchTerm,
    settings,
    setSettings,
    setModalState,
    fileSystem,
    setSelectedFolderId,
    setSelectedSpecialView,
    setSelectedPromptId,
  } = usePromptContext();

  const [editable, setEditable] = useState<Prompt>(prompt);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [isNotesOpen, setIsNotesOpen] = useState(!!prompt.description);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [copySignal, setCopySignal] = useState(0);
  const titleRef = useRef<HTMLInputElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  // dirty = Nutzer hat seit dem letzten Speichern etwas geändert
  const dirtyRef = useRef(false);
  const editableRef = useRef(editable);
  editableRef.current = editable;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const flush = useCallback(() => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    onSaveRef.current?.(pickEditable(editableRef.current));
    setSavedAt(new Date());
  }, []);

  // Wechsel auf einen anderen Eintrag: offene Änderungen des alten zuerst sichern
  useEffect(() => {
    if (editableRef.current.id !== prompt.id) {
      flush();
      editableRef.current = prompt;
      setEditable(prompt);
      setSavedAt(null);
      setIsNotesOpen(!!prompt.description);
      if (prompt.name === KIND_META[getKind(prompt)].defaultName) {
        setTimeout(() => titleRef.current?.select(), 60);
      }
    } else if (!dirtyRef.current && !isSameEditable(prompt, editableRef.current)) {
      // Änderung von außen (Rückgängig, Favorit aus der Liste, Versionswiederherstellung)
      editableRef.current = prompt;
      setEditable(prompt);
    }
  }, [prompt, flush]);

  // Beim Verlassen des Editors nichts verlieren
  useEffect(() => () => flush(), [flush]);

  useEffect(() => {
    if (!dirtyRef.current) return;
    const t = setTimeout(flush, AUTOSAVE_DELAY);
    return () => clearTimeout(t);
  }, [editable, flush]);

  const change = useCallback((updates: Partial<Prompt>) => {
    dirtyRef.current = true;
    const next = { ...editableRef.current, ...updates, updatedAt: new Date().toISOString() };
    editableRef.current = next;
    setEditable(next);
  }, []);

  const saveNow = useCallback(() => {
    dirtyRef.current = true;
    flush();
    showToast('Gespeichert');
  }, [flush, showToast]);

  // Strg/Cmd+S speichert sofort
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveNow();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saveNow]);

  useEffect(() => {
    if (!isMoreOpen) return;
    const onDown = (e: MouseEvent) => { if (moreRef.current && !moreRef.current.contains(e.target as Node)) setIsMoreOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsMoreOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [isMoreOpen]);

  const kind = getKind(editable);
  const meta = KIND_META[kind];

  const parentFolderId = useMemo(() => findPromptAndParentFolder(fileSystem, editable.id)?.parent.id || null, [fileSystem, editable.id]);
  const activeItems = useMemo(() => getAllPrompts(fileSystem).filter(p => !p.deletedAt), [fileSystem]);
  const resolve = useCallback((id: string) => activeItems.find(p => p.id === id), [activeItems]);

  const navigateFolder = (id: string) => {
    setSelectedSpecialView(null);
    setSelectedFolderId(id);
  };

  const toggleFavorite = () => {
    const next = !editableRef.current.isFavorite;
    change({ isFavorite: next });
    dirtyRef.current = true;
    // Favorit sofort speichern, damit Liste und Seitenleiste mitziehen
    setTimeout(flush, 0);
  };

  const convertTo = (target: ItemKind) => {
    const updates: Partial<Prompt> = { kind: target };
    if (target === 'workflow' && !(editable.steps && editable.steps.length)) {
      updates.steps = [{ id: uuidv4(), title: '', content: editable.content || '', refId: null }];
    }
    if (target === 'skill') {
      if (!editable.slug) updates.slug = slugify(editable.name);
      if (!editable.files) updates.files = [];
    }
    if (target === 'gem' && !editable.files) updates.files = [];
    change(updates);
    showToast(`In ${KIND_META[target].label} umgewandelt. Bisherige Inhalte bleiben erhalten.`);
  };

  const copyText = (text: string, message: string) => {
    navigator.clipboard.writeText(text)
      .then(() => { showToast(message); onTrackUse?.(editable.id); })
      .catch(() => showToast('Kopieren nicht möglich. Bitte Zugriff auf die Zwischenablage erlauben.', 'error'));
  };

  const handlePrimaryCopy = () => {
    if (kind === 'skill') return copyText(buildSkillMarkdown(editable), 'SKILL.md kopiert');
    if (kind === 'workflow') return copyText(buildWorkflowMarkdown(editable, resolve), 'Workflow als Markdown kopiert');
    if (kind === 'gem') return copyText(editable.content || '', 'Systemprompt kopiert');
    // Prompts laufen über PromptActions, damit Platzhalter abgefragt werden
    setCopySignal(n => n + 1);
  };

  const primaryCopyLabel = kind === 'skill' ? 'SKILL.md kopieren' : kind === 'workflow' ? 'Workflow kopieren' : kind === 'gem' ? 'Systemprompt kopieren' : 'Prompt kopieren';
  const notesLabel = kind === 'workflow' ? 'Ziel' : kind === 'gem' ? 'Beschreibung' : 'Notizen';
  // Workflows und Gems zeigen das Feld immer: Ziel bzw. Beschreibung gehören dort zum Eintrag
  const notesAlwaysOpen = kind === 'workflow' || kind === 'gem';

  const contentEditor = (label: string, placeholder: string) => (
    <PromptActions
      promptId={editable.id}
      content={editable.content}
      searchTerm={searchTerm}
      settings={settings}
      onChangeContent={(content) => change({ content })}
      onUpdateSettings={(partial) => setSettings?.((prev: any) => ({ ...(prev || {}), ...partial }))}
      onTrackPromptUse={(id) => onTrackUse?.(id)}
      label={label}
      placeholderText={placeholder}
      copySignal={copySignal}
    />
  );

  const menuItem = 'w-full flex items-center gap-3 px-3 py-2 text-sm text-ink hover:bg-ink/[0.06] text-left';

  return (
    <article className="h-full flex flex-col bg-raised/40 overflow-hidden" aria-label={`${meta.label} bearbeiten`}>
      {/* Kopf */}
      <header className="shrink-0 px-4 md:px-8 pt-3 pb-3 border-b border-rule bg-sheet">
        <div className="flex items-center gap-3 min-h-[28px]">
          {isMobile && (
            <button onClick={onClose} className="btn btn-ghost btn-sm !px-2 -ml-2">
              <ArrowLeftIcon className="h-4 w-4" /> Liste
            </button>
          )}
          <span className={`flex items-center gap-1.5 kind-label ${meta.text}`}>
            <KindGlyph kind={kind} className="h-3.5 w-3.5" /> {meta.label}
          </span>
          {parentFolderId && <Breadcrumbs fileSystem={fileSystem} folderId={parentFolderId} onNavigate={navigateFolder} />}
          <span className="ml-auto font-mono text-[11px] text-graphite whitespace-nowrap" role="status" aria-live="polite">
            {savedAt
              ? `gespeichert ${savedAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
              : (() => {
                  if (!editable.updatedAt) return '';
                  const d = new Date(editable.updatedAt);
                  return Number.isNaN(d.getTime()) ? '' : `geändert ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}`;
                })()}
          </span>
        </div>

        <div className="flex flex-col xl:flex-row xl:items-center gap-3 mt-2">
          <label htmlFor="item-title" className="sr-only">Titel</label>
          <input
            id="item-title"
            ref={titleRef}
            type="text"
            value={editable.name}
            onChange={(e) => change({ name: e.target.value })}
            placeholder="Titel"
            className="flex-1 min-w-0 bg-transparent border-0 border-b-2 shadow-none focus:ring-0 px-0 font-display text-[26px] leading-tight font-semibold text-ink placeholder:text-graphite/60 focus:outline-none border-b-2 border-transparent focus:border-rule pb-0.5"
          />

          <div className="flex items-center gap-1 shrink-0">
            <button onClick={handlePrimaryCopy} className="btn btn-primary btn-sm" title={`${primaryCopyLabel} (wird als Nutzung gezählt)`}>
              <ClipboardCopyIcon className="h-4 w-4" /> {primaryCopyLabel}
            </button>
            <button
              onClick={toggleFavorite}
              className={`icon-btn ${editable.isFavorite ? '!text-amber-500' : ''}`}
              aria-pressed={editable.isFavorite}
              aria-label={editable.isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
              title={editable.isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
            >
              <StarIcon filled={editable.isFavorite} className="h-[18px] w-[18px]" />
            </button>
            <button onClick={() => { flush(); onExport?.(editableRef.current); }} className="icon-btn" aria-label="Herunterladen" title={kind === 'skill' ? 'SKILL.md und Zusatzdateien herunterladen' : kind === 'workflow' ? 'Als Markdown herunterladen' : kind === 'gem' ? 'Systemprompt und Wissensdateien herunterladen' : 'Als JSON herunterladen'}>
              <DownloadIcon className="h-[18px] w-[18px]" />
            </button>
            <div className="relative" ref={moreRef}>
              <button onClick={() => setIsMoreOpen(o => !o)} className="icon-btn" aria-haspopup="menu" aria-expanded={isMoreOpen} aria-label="Weitere Aktionen" title="Weitere Aktionen">
                <MoreVerticalIcon className="h-[18px] w-[18px]" />
              </button>
              {isMoreOpen && (
                <div className="context-menu absolute right-0 top-full mt-1 w-60 animate-scale-fade-in" role="menu">
                  <button role="menuitem" className={menuItem} onClick={() => { flush(); onDuplicate?.(editableRef.current); setIsMoreOpen(false); }}>
                    <CopyPlusIcon className="h-4 w-4 text-graphite" /> Duplizieren
                  </button>
                  <button role="menuitem" className={menuItem} onClick={() => { flush(); setModalState({ type: 'save-as-template', promptId: editable.id }); setIsMoreOpen(false); }}>
                    <StampIcon className="h-4 w-4 text-graphite" /> Als Vorlage speichern
                  </button>
                  <button role="menuitem" className={menuItem} onClick={() => { flush(); setModalState({ type: 'move-prompt', promptId: editable.id }); setIsMoreOpen(false); }}>
                    <FolderMoveIcon className="h-4 w-4 text-graphite" /> In anderen Ordner verschieben
                  </button>
                  <div className="border-t border-rule my-1" role="separator" />
                  <button role="menuitem" className={`${menuItem} !text-red-700 dark:!text-red-400`} onClick={() => { flush(); onDelete?.(); setIsMoreOpen(false); }}>
                    <TrashIcon className="h-4 w-4" /> In den Papierkorb
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Inhalt */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-[880px] mx-auto px-4 md:px-8 py-6 space-y-7">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <KindSwitch value={kind} onChange={convertTo} />
            <div className="flex-1 min-w-[16rem]">
              <TagManager tags={editable.tags} content={editable.content} onChange={(tags) => change({ tags })} />
            </div>
          </div>

          {kind === 'skill' && <SkillHeader item={editable} onChange={change} />}

          {kind !== 'skill' && (
            <section>
              {isNotesOpen || notesAlwaysOpen ? (
                <>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="item-notes" className="label-xs">{notesLabel}</label>
                    {!notesAlwaysOpen && !editable.description && (
                      <button onClick={() => setIsNotesOpen(false)} className="text-xs text-graphite hover:text-ink">Ausblenden</button>
                    )}
                  </div>
                  <textarea
                    id="item-notes"
                    value={editable.description ?? ''}
                    onChange={(e) => change({ description: e.target.value })}
                    placeholder={kind === 'workflow' ? 'Wozu dient dieser Ablauf und was liegt am Ende vor?' : kind === 'gem' ? 'Wofür ist dieses Gem da? Wird nicht mitkopiert.' : 'Kontext, Einsatzzweck, Erfahrungen mit diesem Prompt. Wird nicht mitkopiert.'}
                    rows={2}
                    className="textarea !min-h-[64px] !text-sm"
                  />
                </>
              ) : (
                <button onClick={() => setIsNotesOpen(true)} className="text-sm text-graphite hover:text-ink">
                  + Notiz hinzufügen
                </button>
              )}
            </section>
          )}

          {kind === 'prompt' && contentEditor('Prompt-Text', 'Prompt hier schreiben oder einfügen. Platzhalter wie {{thema}} werden beim Kopieren abgefragt.')}

          {kind === 'skill' && (
            <>
              {contentEditor('Anweisungen · Inhalt der SKILL.md', '# Name des Skills\n\nSchritt für Schritt beschreiben, wie der Assistent vorgehen soll …')}
              <SkillFiles item={editable} onChange={change} onToast={showToast} />
              <SkillPreview item={editable} />
            </>
          )}

          {kind === 'gem' && (
            <>
              {contentEditor('Systemprompt · Anweisungen des Gems', 'Rolle, Arbeitsweise und Regeln des Gems. Wird in der Gemini-App unter „Anweisungen“ eingefügt.')}
              <SkillFiles
                item={editable}
                onChange={change}
                onToast={showToast}
                title="Wissensdateien"
                emptyHint="Kompendien, Kataloge oder Beispiele, die im Gem unter „Wissen“ hochgeladen werden."
              />
            </>
          )}

          {kind === 'workflow' && (
            <WorkflowEditor
              steps={editable.steps || []}
              onChange={(steps) => change({ steps })}
              candidates={activeItems.filter(p => p.id !== editable.id && getKind(p) !== 'workflow')}
              onOpenItem={(id) => { flush(); setSelectedPromptId(id); }}
            />
          )}

          {kind === 'workflow' && (editable.files || []).length > 0 && (
            <SkillFiles
              item={editable}
              onChange={change}
              onToast={showToast}
              title="Originaldatei"
            />
          )}

          {kind !== 'workflow' && (
            <div id="version-history">
              <VersionHistory
                versions={editable.versions || []}
                content={editable.content}
                onChangeVersions={(versions) => change({ versions })}
                onChangeContent={(content) => change({ content })}
              />
            </div>
          )}

          <p className="font-mono text-[11px] text-graphite pt-2 border-t border-rule">
            {kind === 'skill' && <>name: {getSkillSlug(editable)} · </>}
            {prompt.useCount || 0}× kopiert
            {prompt.lastUsed && <> · zuletzt {new Date(prompt.lastUsed).toLocaleDateString('de-DE')}</>}
            {editable.createdAt && <> · angelegt {new Date(editable.createdAt).toLocaleDateString('de-DE')}</>}
            {' · '}<kbd className="kbd !h-4 !text-[10px]">Strg</kbd>+<kbd className="kbd !h-4 !text-[10px]">S</kbd> speichert sofort
          </p>
        </div>
      </div>
    </article>
  );
};

export default PromptEditor;
